/**
 * Welcome-modal hooks. `useWelcome` reads the show/invitesRemaining state from
 * /api/auth/me; `useDismissWelcome` posts the dismiss and flips the cache so the
 * modal closes immediately and never reappears this session.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import { getWelcomeState, dismissWelcome, type WelcomeState } from '@/api/usersApi';

const WELCOME_KEY = ['welcome'] as const;

export function useWelcome() {
  const { isAuthenticated, isSdkReady } = useAuth();
  return useQuery<WelcomeState>({
    queryKey: WELCOME_KEY,
    queryFn: () => getWelcomeState(),
    enabled: isAuthenticated && isSdkReady,
    staleTime: 60_000,
  });
}

export function useDismissWelcome() {
  const queryClient = useQueryClient();
  return useMutation<{ dismissed: boolean }, Error, void>({
    mutationFn: () => dismissWelcome(),
    onSuccess: () => {
      queryClient.setQueryData<WelcomeState>(WELCOME_KEY, (prev) =>
        prev ? { ...prev, show: false } : prev);
    },
  });
}
